import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef, NZ_MODAL_DATA, NzModalService } from 'ng-zorro-antd/modal';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core';
import { HttpService } from 'src/app/http/http.service';
import { PaymentIframeModalComponent } from 'src/app/shared/components/payment-iframe-modal/payment-iframe-modal.component';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { PaymentOrderType, buildPaymentResultUrl, storePaymentSessionSnapshot } from 'src/app/shared/utils/payment-flow.util';
import { ConstantUtil } from 'src/app/util/constant.util';

type PurchaseMode = 'buy' | 'renew';
const ROBOT_PRICING_CODE = 'AI_LIMIT';
type BalanceType = 'uupon';

interface SelectedPricing {
  pricingConfigId: string;
  pricingItemId: string;
  pricingCode: string;
}

interface BalanceCard {
  type: BalanceType;
  points: number;
  deductRate: number;
}

interface RobotPricingPackageItem {
  id?: string;
  pricingConfigId?: string;
  pricingCode?: string;
  durationMonths?: number;
  priceCny?: number;
  discountPriceCny?: number;
  discountRate?: number;
  trialEnabled?: boolean;
  trialDays?: number;
  description?: string;
}

@Component({
  selector: 'robot-service-purchase',
  standalone: true,
  templateUrl: './robot-service-purchase.component.html',
  styleUrls: ['./robot-service-purchase.component.less'],
  imports: [CommonModule, FormsModule, I18nPipe, NzSpinModule, NzButtonModule, NzIconModule, NzTagModule, TestIdDirective]
})
export class RobotServicePurchaseComponent implements OnInit {
  private readonly http = inject(HttpService);
  private readonly modalRef = inject(NzModalRef);
  private readonly modalService = inject(NzModalService);
  private readonly message = inject(NzMessageService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  readonly modalData: any = inject(NZ_MODAL_DATA, { optional: true }) || {};

  loading = false;
  submitting = false;

  mode: PurchaseMode = 'buy';
  robotId: string | null = null;
  robotName = '';
  robotNameTouched = false;
  private aipoolTenantId = '';

  packages: any[] = [];
  selectedPricing: SelectedPricing | null = null;
  balanceCards: BalanceCard[] = [];
  viewStep: 'packages' | 'payment' = 'packages';
  deductionInputs: Record<BalanceType, number | null> = {
    uupon: null
  };

  ngOnInit(): void {
    this.mode = (this.modalData?.mode as PurchaseMode) || 'buy';
    this.robotId = this.modalData?.robotId ?? null;
    this.robotName = String(this.modalData?.name ?? '').trim();
    this.viewStep = this.modalData?.initialStep === 'payment' ? 'payment' : 'packages';
    this.loadBalanceSummary();
    this.loadPackages();
    this.notifyViewStep();
  }

  getResolvedIdentityType(): 'VirtualPersona' | 'VirtualEmployee' {
    return this.modalData?.serviceNumberType === 'Boss' ? 'VirtualPersona' : 'VirtualEmployee';
  }

  getIdentityTypeLabel(): string {
    return this.i18n.fanyi(
      this.getResolvedIdentityType() === 'VirtualPersona'
        ? 'robot-service.identity.virtual-persona'
        : 'robot-service.identity.virtual-employee'
    );
  }

  hasValidRobotName(): boolean {
    return String(this.robotName ?? '').trim().length > 0;
  }

  shouldShowRobotNameError(): boolean {
    return this.robotNameTouched && !this.hasValidRobotName();
  }

  markRobotNameTouched(): void {
    this.robotNameTouched = true;
  }

  canBackToPackages(): boolean {
    return this.modalData?.initialStep !== 'payment';
  }

  private loadBalanceSummary(): void {
    this.balanceCards = this.buildBalanceCards();
    this.http.post(ConstantUtil.ADMIN_ROBOT_ORDER_V1_POINTS, {}).subscribe({
      next: res => {
        const isSuccess = !!res?.success || res?.status === 0 || res?.status === 200;
        if (!isSuccess) return;
        this.aipoolTenantId = this.resolveAipoolTenantId(res?.data);
        this.balanceCards = this.buildBalanceCards(res?.data);
        this.deductionInputs = { uupon: null };
      },
      error: () => {}
    });
  }

  loadPackages(): void {
    this.loading = true;
    const apiPath = this.modalData?.packageListApi || ConstantUtil.ADMIN_PRICING_PACKAGE_ITEM_V1_LIST;
    const payload = apiPath === ConstantUtil.ADMIN_PRICING_PACKAGE_ITEM_V1_LIST ? { pricingCode: ROBOT_PRICING_CODE } : {};
    this.http
      .post(apiPath, payload)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: res => {
          if (!res?.success) return;
          const list = this.normalizePackageList(res?.data);
          const trialUsed = this.modalData?.trialUsed === true;
          const filteredList = trialUsed ? list.filter(item => !this.isTrialPackage(item)) : list;
          this.packages = filteredList;
          const preferredSelection = this.normalizeSelectedPricing(this.modalData?.selectedPricing);
          const preferred = this.resolvePreferredPackage(preferredSelection);
          const recommended = this.packages.find(p => !!p?.recommend) || null;
          this.selectedPricing =
            this.extractSelectedPricing(preferred) ??
            this.extractSelectedPricing(recommended) ??
            this.extractSelectedPricing(this.packages[0]);
          this.applyInitialStep();
        },
        error: () => {}
      });
  }

  private normalizeSelectedPricing(raw?: Partial<SelectedPricing> | null): SelectedPricing | null {
    const pricingConfigId = String(raw?.pricingConfigId ?? '').trim();
    const pricingItemId = String(raw?.pricingItemId ?? '').trim();
    const pricingCode = String(raw?.pricingCode ?? '').trim();
    if (!pricingItemId && !pricingCode) {
      return null;
    }
    return {
      pricingConfigId,
      pricingItemId,
      pricingCode
    };
  }

  private extractSelectedPricing(item: any): SelectedPricing | null {
    const pricingConfigId = String(item?.pricingConfigId ?? '').trim();
    const pricingItemId = String(item?.pricingItemId ?? item?.id ?? '').trim();
    const pricingCode = String(item?.pricingCode ?? '').trim();
    if (!pricingItemId || !pricingCode) {
      return null;
    }
    return {
      pricingConfigId,
      pricingItemId,
      pricingCode
    };
  }

  private matchesSelectedPricing(item: any, selectedPricing: SelectedPricing): boolean {
    const targetItemId = String(selectedPricing?.pricingItemId ?? '').trim();
    const targetCode = String(selectedPricing?.pricingCode ?? '').trim();
    const itemId = String(item?.pricingItemId ?? item?.id ?? '').trim();
    const itemCode = String(item?.pricingCode ?? '').trim();
    if (targetItemId) {
      return itemId === targetItemId;
    }
    if (targetCode) {
      return itemCode === targetCode;
    }
    return false;
  }

  private resolvePreferredPackage(selectedPricing: SelectedPricing | null): any | null {
    if (!selectedPricing) {
      return null;
    }
    const targetItemId = String(selectedPricing.pricingItemId ?? '').trim();
    if (targetItemId) {
      return this.packages.find(item => String(item?.pricingItemId ?? item?.id ?? '').trim() === targetItemId) ?? null;
    }
    const targetCode = String(selectedPricing.pricingCode ?? '').trim();
    if (!targetCode) {
      return null;
    }
    const matched = this.packages.filter(item => String(item?.pricingCode ?? '').trim() === targetCode);
    return matched.length === 1 ? matched[0] : null;
  }

  private applyInitialStep(): void {
    if (this.modalData?.initialStep !== 'payment') {
      return;
    }
    if (!this.selectedPricing?.pricingItemId || this.isTrialPackage(this.getSelectedPackage())) {
      return;
    }
    this.viewStep = 'payment';
    this.notifyViewStep();
  }

  isSelected(item: any): boolean {
    return !!this.selectedPricing && this.matchesSelectedPricing(item, this.selectedPricing);
  }

  isTrialPackage(item: any): boolean {
    return !!item?.trial;
  }

  isRecommended(item: any): boolean {
    return !!item?.recommend;
  }

  selectPackage(item: any): void {
    this.markRobotNameTouched();
    if (!this.hasValidRobotName()) {
      this.message.error(this.i18n.fanyi('robot-service.purchase.name-required'));
      return;
    }
    this.selectedPricing = this.extractSelectedPricing(item);
    if (this.isTrialPackage(item)) {
      this.submit();
      return;
    }
    this.viewStep = 'payment';
    this.notifyViewStep();
    this.deductionInputs = { uupon: null };
  }

  getPackageTitle(item: any): string {
    return String(item?.title ?? item?.description ?? '').trim();
  }

  getPackageDesc(item: any): string {
    return String(item?.subTitle ?? '').trim();
  }

  getTrialDescList(item: any): string[] {
    const desc = this.getPackageDesc(item);
    return desc ? [desc] : [];
  }

  getPriceText(item: any): string {
    if (this.isTrialPackage(item)) return '¥0';
    const priceCny = item?.priceCny;
    if (priceCny === null || priceCny === undefined) return '';
    return `¥${String(priceCny)}`;
  }

  getOriginalPriceText(item: any): string {
    if (this.isTrialPackage(item)) return '';
    const originalPriceCny = item?.originalPriceCny;
    if (originalPriceCny === null || originalPriceCny === undefined) return '';
    return `¥${String(originalPriceCny)}`;
  }

  shouldShowOriginalPrice(item: any): boolean {
    if (this.isTrialPackage(item)) return false;
    const currentPrice = Number(item?.priceCny ?? NaN);
    const originalPrice = Number(item?.originalPriceCny ?? NaN);
    return Number.isFinite(currentPrice) && Number.isFinite(originalPrice) && originalPrice > currentPrice;
  }

  getBalanceNameKey(type: BalanceType): string {
    return 'robot-service.purchase.balance.uupon';
  }

  getBalanceCardClass(type: BalanceType): string {
    return 'balance-card-uupon';
  }

  getBalancePoints(type: BalanceType): number {
    return this.balanceCards.find(item => item.type === type)?.points ?? 0;
  }

  formatPoints(value: number): string {
    return Number(value || 0).toLocaleString('en-US');
  }

  getDeductAmount(points: number, rate: number): string {
    const amount = Number(points || 0) / Number(rate || 1);
    return amount.toFixed(2);
  }

  getSelectedPackage(): any | null {
    return this.packages.find(item => !!this.selectedPricing && this.matchesSelectedPricing(item, this.selectedPricing)) || null;
  }

  getSelectedPackagePrice(): number {
    const price = Number(this.getSelectedPackage()?.priceCny ?? 0);
    return Number.isFinite(price) && price > 0 ? price : 0;
  }

  getDeductedCash(type: BalanceType): number {
    const card = this.balanceCards.find(item => item.type === type);
    if (!card) return 0;
    const points = Number(this.deductionInputs[type] ?? 0);
    if (!Number.isFinite(points) || points <= 0) return 0;
    return points / card.deductRate;
  }

  getTotalDeductedCash(): number {
    const sum = this.getDeductedCash('uupon');
    return Math.min(this.getSelectedPackagePrice(), sum);
  }

  getPayableCash(): number {
    return Math.max(0, this.getSelectedPackagePrice() - this.getTotalDeductedCash());
  }

  getDeductDisplay(type: BalanceType): string {
    return this.getDeductedCash(type).toFixed(2);
  }

  getDeductPoints(type: BalanceType): number {
    return this.toNonNegativeInteger(this.deductionInputs[type]);
  }

  getPayableDisplay(): string {
    return this.getPayableCash().toFixed(2);
  }

  blockNonIntegerInput(event: InputEvent): void {
    if (event.data && !/^\d+$/.test(event.data)) {
      event.preventDefault();
    }
  }

  onDeductInput(type: BalanceType, event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) return;
    const integerText = input.value.replace(/\D+/g, '');
    this.applyDeductInput(type, integerText);
    input.value = this.deductionInputs[type] === null ? '' : String(this.deductionInputs[type]);
  }

  applyDeductInput(type: BalanceType, value: string | number | null | undefined): void {
    if (this.isDeductInputDisabled(type)) {
      this.deductionInputs[type] = null;
      return;
    }
    const card = this.balanceCards.find(item => item.type === type);
    if (!card) return;
    const raw = Number(value ?? 0);
    if (!Number.isFinite(raw) || raw <= 0) {
      this.deductionInputs[type] = null;
      return;
    }
    const packagePrice = this.getSelectedPackagePrice();
    if (raw / card.deductRate >= packagePrice) {
      const maxPointsByPackage = Math.floor(packagePrice * card.deductRate);
      const points = Math.min(card.points, maxPointsByPackage);
      this.deductionInputs[type] = this.normalizeUUPonMultiple(points);
      this.syncDeductionLock(type);
      return;
    }
    const maxByBalance = card.points;
    const maxByPrice = Math.floor(this.getRemainingCashFor(type) * card.deductRate);
    const normalized = Math.floor(Math.min(raw, maxByBalance, maxByPrice));
    this.deductionInputs[type] = normalized > 0 ? normalized : null;
    this.syncDeductionLock(type);
  }

  deductAll(type: BalanceType): void {
    if (this.isDeductInputDisabled(type)) {
      this.deductionInputs[type] = null;
      return;
    }
    const card = this.balanceCards.find(item => item.type === type);
    if (!card) return;
    const packagePrice = this.getSelectedPackagePrice();
    const maxPointsByPackage = Math.floor(packagePrice * card.deductRate);
    if (card.points >= maxPointsByPackage) {
      this.deductionInputs[type] = this.normalizeUUPonMultiple(maxPointsByPackage);
      this.syncDeductionLock(type);
      return;
    }
    const maxPointsByPrice = Math.floor(this.getRemainingCashFor(type) * card.deductRate);
    const points = Math.min(card.points, maxPointsByPrice);
    this.deductionInputs[type] = this.normalizeUUPonMultiple(points);
    this.syncDeductionLock(type);
  }

  backToPackages(): void {
    if (!this.canBackToPackages()) {
      return;
    }
    this.viewStep = 'packages';
    this.notifyViewStep();
    this.deductionInputs = { uupon: null };
  }

  confirmPay(): void {
    if (this.submitting) {
      return;
    }
    this.markRobotNameTouched();
    if (!this.hasValidRobotName()) {
      this.message.error(this.i18n.fanyi('robot-service.purchase.name-required'));
      return;
    }
    if (!this.validateUUPonMultiple()) {
      return;
    }
    if (!this.selectedPricing?.pricingItemId || this.isTrialPackage(this.getSelectedPackage())) return;
    this.submit();
  }

  private setSubmitting(value: boolean): void {
    this.submitting = value;
  }

  private getRemainingCashFor(type: BalanceType): number {
    const packagePrice = this.getSelectedPackagePrice();
    return Math.max(0, packagePrice);
  }

  isDeductInputDisabled(type: BalanceType): boolean {
    return false;
  }

  private syncDeductionLock(type: BalanceType): void {
    return;
  }

  private normalizeUUPonMultiple(points: number): number | null {
    const normalized = Math.floor(Number(points) || 0);
    const multiple = normalized - (normalized % 4);
    return multiple > 0 ? multiple : null;
  }

  private validateUUPonMultiple(): boolean {
    const points = this.getDeductPoints('uupon');
    if (points > 0 && points % 4 !== 0) {
      this.message.error(this.i18n.fanyi('robot-service.purchase.deduct.multiple-of-four'));
      return false;
    }
    return true;
  }

  private notifyViewStep(): void {
    const cb = this.modalData?.onViewStepChange;
    if (typeof cb === 'function') {
      cb(this.viewStep);
    }
  }

  submit(): void {
    if (this.submitting) {
      return;
    }
    if (!this.validateUUPonMultiple()) {
      return;
    }
    if (!this.selectedPricing?.pricingItemId) return;
    const tenantId = String(this.modalData?.tenantId ?? '').trim();
    if (!tenantId) {
      this.message.error(this.i18n.fanyi('robot-service.purchase.tenant-id-required'));
      return;
    }
    this.markRobotNameTouched();
    const name = String(this.robotName ?? '').trim();
    if (!name) {
      this.message.error(this.i18n.fanyi('robot-service.purchase.name-required'));
      return;
    }
    const existingOrderId = String(this.modalData?.orderId ?? '').trim();
    if (existingOrderId) {
      this.setSubmitting(true);
      this.payOrder(existingOrderId, tenantId);
      return;
    }
    this.setSubmitting(true);
    let handOffToPay = false;
    const apiPath = this.modalData?.orderCreateApi || ConstantUtil.ADMIN_ROBOT_ORDER_V1_CREATE;
    const selectedPackage = this.getSelectedPackage();
    const isTrial = this.isTrialPackage(selectedPackage);
    const selectedPricing = this.extractSelectedPricing(selectedPackage);
    if (!selectedPricing?.pricingConfigId || !selectedPricing?.pricingItemId || !selectedPricing?.pricingCode) {
      this.setSubmitting(false);
      this.message.error(this.i18n.fanyi('robot-service.purchase.package-required'));
      return;
    }
    const payload: any = {
      pricingConfigId: selectedPricing.pricingConfigId,
      pricingItemId: selectedPricing.pricingItemId,
      pricingCode: selectedPricing.pricingCode,
      useTrial: isTrial
    };
    payload.tenantId = tenantId;
    if (this.aipoolTenantId) {
      payload.aipoolTenantId = this.aipoolTenantId;
    }
    payload.identityType = this.getResolvedIdentityType();
    payload.name = name;
    if (!isTrial) {
      payload.deductUUPonPoints = this.getDeductPoints('uupon');
    }
    if (this.robotId) {
      payload.robotId = this.robotId;
    }
    this.http
      .post(apiPath, payload)
      .pipe(
        finalize(() => {
          if (!handOffToPay) {
            this.setSubmitting(false);
          }
        })
      )
      .subscribe({
        next: res => {
          if (!res?.success) return;
          const raw = res.data ?? res;
          const orderId = this.resolveOrderId(raw);
          const payRequired = this.isPayRequired(raw);
          if (payRequired && orderId) {
            handOffToPay = true;
            this.payOrder(orderId, tenantId);
            return;
          }
          this.setSubmitting(false);
          this.openPaymentUrl(raw, orderId);
          this.modalRef.close({ refresh: true });
        },
        error: () => {
          this.setSubmitting(false);
        }
      });
  }

  close(): void {
    this.modalRef.close();
  }

  private buildBalanceCards(raw?: any): BalanceCard[] {
    return [{ type: 'uupon', points: this.toNonNegativeInteger(raw?.uuponPoint), deductRate: 4 }];
  }

  private resolveAipoolTenantId(raw?: any): string {
    return String(raw?.aipoolTenantId ?? '').trim();
  }

  private payOrder(orderId: string, tenantId: string): void {
    const apiPath = ConstantUtil.ADMIN_ROBOT_ORDER_V1_PAY.replace('{orderId}', encodeURIComponent(orderId));
    this.setSubmitting(true);
    this.http.post(apiPath, this.buildPayPayload(tenantId, orderId)).subscribe({
      next: res => {
        if (!res?.success) {
          this.setSubmitting(false);
          return;
        }
        const payload = res?.data ?? res;
        this.storePaymentSnapshot('robot', orderId, payload);
        this.setSubmitting(false);
        this.openPaymentUrl(payload, orderId);
      },
      error: () => {
        this.setSubmitting(false);
      }
    });
  }

  private buildPayPayload(tenantId: string, orderId?: string): any {
    const urls = this.buildPaymentReturnUrls('robot', tenantId, orderId);
    const payload: any = {
      tenantId,
      resultURL: urls.resultURL,
      cancelURL: urls.cancelURL
    };
    if (this.aipoolTenantId) {
      payload.aipoolTenantId = this.aipoolTenantId;
    }
    return payload;
  }

  private buildPaymentReturnUrls(
    orderType: PaymentOrderType,
    tenantId: string,
    orderId?: string
  ): { resultURL: string; cancelURL: string } {
    return {
      resultURL: this.buildPaymentReturnUrl(orderType, 'success', tenantId, orderId),
      cancelURL: this.buildPaymentReturnUrl(orderType, 'failed', tenantId, orderId)
    };
  }

  private buildPaymentReturnUrl(
    orderType: PaymentOrderType,
    paymentResult: 'success' | 'failed',
    tenantId: string,
    orderId?: string
  ): string {
    return buildPaymentResultUrl({
      orderType,
      paymentResult,
      tenantId,
      aipoolTenantId: this.aipoolTenantId,
      orderId,
      targetId: String(this.robotId ?? '').trim(),
      sourcePath: this.getSourcePath()
    });
  }

  private openPaymentUrl(raw?: any, orderId?: string): void {
    const payUrl = raw?.paymentUrl ?? null;
    if (payUrl) {
      this.openPaymentModal(String(payUrl), orderId);
      return;
    }
    if (this.isPaymentPaid(raw)) {
      this.modalRef.close({ refresh: true });
    }
  }

  private openPaymentModal(paymentUrl: string, orderId?: string): void {
    const paymentModal = this.modalService.create({
      nzContent: PaymentIframeModalComponent,
      nzWidth: '1200px',
      nzFooter: null,
      nzClosable: false,
      nzMaskClosable: false,
      nzClassName: 'payment-iframe-modal',
      nzData: {
        paymentUrl,
        orderId,
        orderType: 'robot'
      }
    });

    paymentModal.afterClose.subscribe(() => {
      this.modalRef.close({ refresh: true });
    });
  }

  private getSourcePath(): string {
    return `${window.location.pathname}${window.location.search}`;
  }

  private storePaymentSnapshot(orderType: PaymentOrderType, orderId: string, raw?: any): void {
    storePaymentSessionSnapshot({
      orderType,
      orderId,
      targetId: String(this.robotId ?? raw?.robotId ?? raw?.targetId ?? '').trim() || undefined,
      tenantId: String(this.modalData?.tenantId ?? '').trim(),
      aipoolTenantId: String(this.aipoolTenantId ?? raw?.aipoolTenantId ?? '').trim() || undefined,
      paymentUrl: String(raw?.paymentUrl ?? '').trim() || undefined,
      reserveTimeoutSeconds: raw?.reserveTimeoutSeconds,
      payStartTime: raw?.payStartTime,
      payExpireTime: raw?.payExpireTime,
      orderStatus: raw?.orderStatus,
      paymentStatus: raw?.paymentStatus,
      payRequired: raw?.payRequired
    });
  }

  private resolveOrderId(raw?: any): string {
    return String(raw?.orderId ?? '').trim();
  }

  private isPayRequired(raw?: any): boolean {
    return raw?.payRequired === true;
  }

  private isPaymentPaid(raw?: any): boolean {
    const statusValues = [raw?.status, raw?.latestOrderStatus, raw?.orderStatus, raw?.paymentStatus].map(value =>
      String(value ?? '')
        .trim()
        .toLowerCase()
    );
    if (statusValues.some(status => ['paid', 'success', 'succeeded', 'completed', 'complete', 'done'].includes(status))) {
      return true;
    }
    if (Number(raw?.status) === 30 || Number(raw?.latestOrderStatus ?? raw?.orderStatus) === 30 || Number(raw?.paymentStatus) === 20) {
      return true;
    }
    return raw?.needPay === false || raw?.payRequired === false;
  }

  private toNonNegativeInteger(value: unknown): number {
    const parsed = Number(value ?? 0);
    if (!Number.isFinite(parsed) || parsed <= 0) return 0;
    return Math.floor(parsed);
  }

  private normalizePackageList(raw: unknown): any[] {
    const list = Array.isArray(raw) ? raw : [];
    return list
      .map((item, index) => this.mapPricingItemToPackage(item as RobotPricingPackageItem, index))
      .filter(Boolean)
      .sort((a, b) => this.comparePackages(a, b));
  }

  private mapPricingItemToPackage(item: RobotPricingPackageItem, index: number): any | null {
    const pricingItemId = String(item?.id ?? '').trim();
    if (!pricingItemId) return null;
    const pricingConfigId = String(item?.pricingConfigId ?? '').trim();
    const pricingCode = String(item?.pricingCode ?? ROBOT_PRICING_CODE).trim() || ROBOT_PRICING_CODE;
    const trial = item?.trialEnabled === true || ((Number(item?.trialDays ?? 0) || 0) > 0 && Number(item?.priceCny ?? 0) <= 0);
    const durationMonths = Number(item?.durationMonths ?? 0) || 0;
    const priceCny = trial ? 0 : this.resolvePackagePrice(item);
    const title = this.resolvePackageTitle(item, trial, durationMonths);
    const subTitle = this.resolvePackageSubtitle(item, trial, durationMonths);

    return {
      id: pricingItemId,
      pricingConfigId,
      pricingItemId,
      pricingCode,
      title,
      subTitle,
      priceCny,
      originalPriceCny: trial ? 0 : this.resolveOriginalPackagePrice(item, priceCny),
      trial,
      trialDays: Number(item?.trialDays ?? 0) || undefined,
      recommend: false,
      order: index,
      durationMonths
    };
  }

  private comparePackages(a: any, b: any): number {
    if (this.isTrialPackage(a) !== this.isTrialPackage(b)) {
      return this.isTrialPackage(a) ? -1 : 1;
    }
    const durationDelta = Number(a?.durationMonths ?? Number.MAX_SAFE_INTEGER) - Number(b?.durationMonths ?? Number.MAX_SAFE_INTEGER);
    if (durationDelta !== 0) {
      return durationDelta;
    }
    return Number(a?.order ?? Number.MAX_SAFE_INTEGER) - Number(b?.order ?? Number.MAX_SAFE_INTEGER);
  }

  private resolvePackagePrice(item: RobotPricingPackageItem): number {
    const discountPrice = Number(item?.discountPriceCny ?? NaN);
    if (Number.isFinite(discountPrice) && discountPrice >= 0) {
      return discountPrice;
    }
    const price = Number(item?.priceCny ?? NaN);
    if (Number.isFinite(price) && price >= 0) {
      return price;
    }
    return 0;
  }

  private resolveOriginalPackagePrice(item: RobotPricingPackageItem, currentPrice: number): number {
    const originalPrice = Number(item?.priceCny ?? NaN);
    if (Number.isFinite(originalPrice) && originalPrice > 0) {
      return originalPrice;
    }
    return currentPrice;
  }

  private resolvePackageTitle(item: RobotPricingPackageItem, trial: boolean, durationMonths: number): string {
    if (trial) {
      return this.getTrialDurationText(Number(item?.trialDays ?? 0) || 0);
    }
    return this.getPackageDurationText(durationMonths);
  }

  private resolvePackageSubtitle(item: RobotPricingPackageItem, trial: boolean, durationMonths: number): string {
    const description = String(item?.description ?? '').trim();
    if (description) {
      return description;
    }
    return trial ? this.getTrialDurationText(Number(item?.trialDays ?? 0) || 0) : this.getPackageDurationText(durationMonths);
  }

  private getPackageDurationText(durationMonths: number): string {
    if (!Number.isFinite(durationMonths) || durationMonths <= 0) {
      return '';
    }
    return `${durationMonths}${this.i18n.fanyi('pricing.package.item.duration.months')}`;
  }

  private getTrialDurationText(trialDays: number): string {
    if (!Number.isFinite(trialDays) || trialDays <= 0) {
      return '';
    }
    return `${trialDays}${this.i18n.fanyi('pricing.package.item.duration.days')}`;
  }
}
